import { useMemo } from 'react';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';

export default function ZoneComparison({ zones = [], limit = 5 }) {
  const topZones = useMemo(() => {
    return zones
      .filter(z => z.overallScore !== null && z.overallScore !== undefined)
      .sort((a, b) => (b.overallScore || 0) - (a.overallScore || 0))
      .slice(0, limit);
  }, [zones, limit]);

  if (topZones.length === 0) {
    return (
      <div className="glass p-6 text-center">
        <p className="text-sm text-slate-400">No zone data available</p>
      </div>
    );
  }

  const getRiskColor = (score) => {
    if (score >= 75) return { bg: 'bg-red-50', text: 'text-red-700', bar: 'bg-red-500' };
    if (score >= 50) return { bg: 'bg-orange-50', text: 'text-orange-700', bar: 'bg-orange-500' };
    if (score >= 25) return { bg: 'bg-amber-50', text: 'text-amber-700', bar: 'bg-amber-400' };
    return { bg: 'bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-500' };
  };

  const getRiskLevel = (score) => {
    if (score >= 75) return 'CRITICAL';
    if (score >= 50) return 'HIGH';
    if (score >= 25) return 'MEDIUM';
    return 'LOW';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') {
      return <ArrowTrendingDownIcon className="w-4 h-4 text-green-500" />;
    }
    if (trend === 'declining') {
      return <ArrowTrendingUpIcon className="w-4 h-4 text-red-500" />;
    }
    return <div className="w-4 h-4 text-slate-400">—</div>;
  };

  return (
    <div className="glass p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">Zone Risk Comparison</h3>

      <div className="space-y-3">
        {topZones.map((zone, idx) => {
          const score = zone.overallScore || 0;
          const colors = getRiskColor(score);
          const riskLevel = getRiskLevel(score);
          const zoneName = zone.zone?.name || zone.name || `Zone ${zone.zoneId || zone.id}`;

          return (
            <div key={zone.zoneId || zone.id} className={`rounded-lg p-3 ${colors.bg}`}>
              {/* Zone Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-xs font-semibold text-slate-500">#{idx + 1}</span>
                  <span className="text-sm font-semibold text-slate-800 truncate">{zoneName}</span>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.text} bg-white`}>
                  {riskLevel}
                </span>
              </div>

              {/* Score Bar */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${colors.bar}`}
                    style={{ width: `${score}%` }}
                  />
                </div>
                <span className="text-sm font-bold w-10 text-right">{Math.round(score)}</span>
              </div>

              {/* Metrics Row */}
              <div className="grid grid-cols-4 gap-2 text-[10px]">
                <div>
                  <p className="text-slate-500">Alert</p>
                  <p className="font-bold text-slate-700">{Math.round(zone.alertFreqScore || 0)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Severity</p>
                  <p className="font-bold text-slate-700">{Math.round(zone.severityScore || 0)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Compliance</p>
                  <p className="font-bold text-slate-700">{Math.round(zone.complianceScore || 0)}</p>
                </div>
                <div className="flex flex-col items-center">
                  <p className="text-slate-500">Trend</p>
                  {getTrendIcon(zone.trend)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Note */}
      {zones.length > limit && (
        <p className="text-xs text-slate-400 mt-3 text-center">
          Showing {limit} of {zones.length} zones
        </p>
      )}
    </div>
  );
}
